import { NativeSupportDetail } from "@/store/reducers/contents";
import { useTrans } from "@/utils/useTrans";
import PlayCircleIcon from "@/v2-components/core/icon/PlayCircleIcon";
import HapticEffectSheet from "@/v2-components/pages/games/detail/HapticEffectSheet";
import { useCallback, useRef, useState } from "react";
import { Trans } from "next-i18next";
import Image from "next/image";
import { Skeleton } from "@/v2-components/ui/skeleton";
import {
  effectMediaKey,
  useEffectVideo,
  useSectionMediaAspect,
} from "@/libs/games/effect-media-aspect";

export default function HapticEffects({
  effects,
  sheet,
}: {
  effects: NativeSupportDetail[];
  /** 햅틱 요약 카드가 시트를 열 때 — 페이지가 상태를 쥔다(`HapticEffectSheet` 의 controlled 모드) */
  sheet?: {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    highlight: string[];
  };
}) {
  const { t } = useTrans(["games"]);
  const total = effects.reduce((acc, cur) => acc + cur.numberOfEffects, 0);
  const featured = effects.filter((effect) => effect.featured);
  // 카드 틀 비율 — 섹션 영상 중 가장 세로로 긴 원본 비율 하나로 통일(근거는 `src/libs/games/effect-media-aspect.ts`).
  const { aspect, report } = useSectionMediaAspect(
    featured.map(effectMediaKey),
  );

  return (
    /**
     * 시안 데스크탑 `29889:101426` · 모바일 `30035:21405`.
     *
     * ★「전체 보기」가 **헤드 우측**으로 올라갔다(종전엔 그리드 아래 가운데 버튼).
     *
     * ## ★두 폭이 같은 컴포넌트의 반응형이 아니다 — 여섯 축이 갈린다
     *
     * 종전 구현은 데스크탑 값만 두고 모바일에 그대로 흘렸다(실측 2026-09-03 · 360 / 1440):
     *
     * ```
     *                  시안 모바일 30035:21405     시안 데스크탑 29889:101426
     *   제목            22/28 #21272a (cg-90)      24/32 #121619 (cg-100)
     *   제목↔설명        20                         12
     *   설명            12/16 #878d96 tr .4        14/18 #697077 tr .25
     *   설명 배치        ★헤드 **밖** · 전체 폭 328   헤드 안 왼쪽 컬럼
     *   전체 보기        12/16 SemiBold #4d5358     14/18 SemiBold #21272a
     *   그리드 갭        row 16 · ★col **12**       24
     *   카드 내부 갭      8                          12
     * ```
     *
     * ★**설명의 배치가 폭마다 다르다** — 모바일은 제목·링크가 한 줄이고 설명이 그 아래
     * 전체 폭을 쓴다. 데스크탑은 제목·설명이 왼쪽에 쌓이고 링크가 오른쪽에서 세로 가운데다.
     * 순서가 `제목 → 설명 → 링크` 로 바뀌는 게 아니라 **링크가 걸치는 행 수**가 달라지는
     * 것이라, DOM 을 두 벌 두지 않고 2열 그리드에 자리를 명시해 한 벌로 만든다
     * (`laptop:row-span-2` = 데스크탑에서 링크가 두 행에 걸쳐 가운데).
     *
     * ⚠️ **카드 비율은 시안(220×140)이 아니라 영상 원본에서 온다**(2026-10-15). 시안 비율로
     * 고정했더니 원본 영상(전부 1080×1080)의 아래쪽 37% — 햅틱 비주얼라이저 — 가 잘렸다.
     * 섹션 영상 중 가장 세로로 긴 비율 하나로 통일한다(`src/libs/games/effect-media-aspect.ts`).
     */
    <div className="flex flex-col gap-5">
      {/* gap-x 는 제목과 링크 사이 **최소** 간격이다(둘 다 자기 폭). */}
      <div className="grid grid-cols-[1fr_auto] items-center gap-x-4 gap-y-5 laptop:gap-y-3">
        {/* `30035:21407` 22/28 cg-90 · `101429` 24/32 cg-100 — 둘 다 Regular. */}
        <h2 className="col-start-1 row-start-1 text-2xl text-coolgray-90 laptop:text-3xl laptop:text-coolgray-100">
          {t("Haptic Effects")}
        </h2>

        {/**
         * `30035:21408` / `101431`.
         * ★데스크탑만 두 행에 걸친다 — 모바일은 제목과 같은 행에만 있다.
         */}
        <div className="col-start-2 row-start-1 laptop:row-span-2 laptop:self-center">
          <HapticEffectSheet effects={effects} {...sheet} />
        </div>

        {/* `30035:21409` 12/16 cg-50 tr .4 전체 폭 · `101430` 14/18 cg-60 tr .25 왼쪽 컬럼. */}
        <p className="col-span-2 col-start-1 row-start-2 text-sm tracking-[0.4px] text-coolgray-50 laptop:col-span-1 laptop:text-base laptop:tracking-[0.25px] laptop:text-coolgray-60">
          <Trans
            t={t}
            i18nKey={`{{effects}} haptic events embedded for next-level realism.`}
            values={{ effects: total }}
          />
        </p>
      </div>

      {/**
       * ★**`featured` 가 0 이면 그리드를 아예 그리지 않는다.**
       *
       * 섹션의 노출 조건은 `hapticEffects.length > 0`(호출처)인데 카드는 `featured` 로
       * 거른다 — 두 술어가 다르다. 그 사이 구간(`전체 > 0` · `featured === 0`)에서
       * 종전엔 **자식 0개 · 높이 0** 인 그리드가 남아 섹션 갭 20 만 더 벌어졌다.
       * 실측(2026-09-03): dev `pioneer-endless-journey` 4개 전부 `featured:false` ·
       * `linktest` 동일 / prod `dungeons-of-eternity` 는 19개 중 4개라 시안의 4카드와 맞는다.
       * 즉 **양쪽 다 실재하는 경로**다. 시안에는 카드 없는 변형이 없다.
       *
       * 섹션 자체를 숨기지는 않는다 — 설명의 총 개수와 「전체 보기」 시트는 `featured` 와
       * 무관하게 **전체 목록**을 보여주므로 그 구간에서도 내용이 있다.
       */}
      {featured.length > 0 && (
        <div className="grid grid-cols-2 gap-x-3 gap-y-4 laptop:grid-cols-4 laptop:gap-6">
          {featured.map((effect) => (
            <EffectItem
              key={effectMediaKey(effect)}
              effect={effect}
              aspect={aspect}
              onAspect={report}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function EffectItem({
  effect,
  aspect,
  onAspect,
}: {
  effect: NativeSupportDetail;
  /** 섹션 공통 카드 비율(width / height). */
  aspect: number;
  onAspect: (key: string, width: number, height: number) => void;
}) {
  const { t } = useTrans(["games"]);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [playing, setPlaying] = useState(false);
  const reportAspect = useCallback(
    (w: number, h: number) => onAspect(effectMediaKey(effect), w, h),
    [onAspect, effect],
  );
  const { ready: videoReady } = useEffectVideo(videoRef, reportAspect);
  const [imageReady, setImageReady] = useState(false);
  const ready = effect.videoLink ? videoReady : imageReady;

  return (
    /* 카드 내부 갭 — 시안 모바일 `30035:21411` 8 · 데스크탑 `101438` 12. */
    <div className="group flex flex-col gap-2 laptop:gap-3">
      <div
        /* radius **8**(시안 `101438`). 비율은 섹션 공통 값 — 시안의 220×140 은 원본을 잘랐다. */
        className="relative w-full overflow-hidden rounded-lg"
        style={{ aspectRatio: aspect }}
        onClick={() => {
          if (playing) {
            setPlaying(false);
            videoRef.current?.pause();
          }
        }}
      >
        {/* 첫 프레임(이미지는 로드)까지 스켈레톤. 실패해도 걷는다(`useEffectVideo`). */}
        {!ready && (effect.videoLink || effect.imageLink) && (
          <Skeleton className="absolute inset-0 rounded-none" />
        )}
        {effect.videoLink ? (
          <video
            src={`${effect.videoLink}#t=0.001`}
            className="absolute inset-0 h-full w-full object-contain"
            controls={false}
            ref={videoRef}
            preload="auto"
            playsInline
            onPlay={() => setPlaying(true)}
            onPause={() => setPlaying(false)}
          />
        ) : effect.imageLink ? (
          <Image
            className="object-cover"
            src={effect.imageLink}
            fill
            alt={effect.category}
            onLoad={() => setImageReady(true)}
            onError={() => setImageReady(true)}
          />
        ) : null}
        {!playing && effect.videoLink && (
          <button
            className="absolute left-1/2 top-1/2 block -translate-x-1/2 -translate-y-1/2 transition-transform duration-300 hover:scale-110"
            onClick={() => {
              videoRef.current?.play();
            }}
          >
            <PlayCircleIcon className="h-8 w-8 text-black invert laptop:h-12 laptop:w-12" />
          </button>
        )}
      </div>
      {/**
       * 시안 `101439` — **한 줄에 좌우 배치**다(종전엔 세로 스택).
       * 이름 14/18 SemiBold `#21272a` — 두 폭 공통.
       *
       * ★**개수 색만 갈린다**: 데스크탑은 이름과 같은 `#21272a`(cg-90)인데
       * 모바일 `30035:21415` 는 `#878d96`(cg-50)이다. 상속으로 두면 모바일이 진해진다.
       */}
      <div className="flex items-center justify-between gap-2 text-coolgray-90">
        <h4 className="truncate text-base font-600 tracking-[0.25px]">
          {effect.category}
        </h4>
        <p className="shrink-0 text-sm tracking-[0.4px] text-coolgray-50 laptop:text-coolgray-90">
          {effect.numberOfEffects > 1 ? (
            <Trans
              t={t}
              i18nKey={`{{effects}} different types`}
              values={{ effects: effect.numberOfEffects }}
            />
          ) : (
            <Trans
              t={t}
              i18nKey={`{{effects}} type`}
              values={{ effects: effect.numberOfEffects }}
            />
          )}
        </p>
      </div>
    </div>
  );
}
